import { ControllerInterface } from "csi/action/Controller";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import ExtendedPath from "csi/core/ExtendedPath";
import Spec from "csi/core/Spec";
import { getRuntimeRulesFromNode, getRuntimeRulesQuery, RuntimeRulesSchema } from "csi/middleware/attributeRule/RuleService";
import { ControllerSaveFunction } from "csi/middleware/common";
import PageStore from "csi/page/PageStore";
import SearchManager from "csi/page/SearchManager";
import { getSearch } from "csi/service/dataStoreService";
import rootStore from "csi/state/rootStore";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, cnl, ICache } from "csi/typings";
import Query from "csi/xml/Query";
import Search from "csi/xml/Search";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { PPWizardStore } from "./NewProductPresentation/PPWizardStore";
import PPProductLayoutStore from "./PPProductLayoutStore";
import { getColorMatrixPath, getPresentationPrimaryType, isPresentationEditor, processProductViolations } from "./PPService";
import PPValidationStore from "./Validation/PPValidationStore";

/** common properties */
export interface IPresentationProps {
	/** query to apply to colors for each product */
	colorQuery: Query;

	/** presentation node */
	presentation: C8Node;

	/** primary node type in PP */
	primaryType: string;

	/** product color node type if applicable */
	productColorType: string;

	/** the url set of violations of color */
	violationColorUrls: Set<cnl>;

	/** the urls of violation, including the ones due to both products and colors */
	violationUrls: Set<cnl>;

	/** the url set of violations due to product */
	violationUrlsOfProduct: Set<cnl>;

	/** the product cards search, including both the valid and invalid ones */
	productCardSearch: Search;
}

export default class PresentationPageStore extends PageStore {
	@observable.ref private _presentationWizardStore: PPWizardStore;
	@observable.ref private _productLayoutStore: PPProductLayoutStore;
	@observable.ref private _validationStore: PPValidationStore;
	@observable.ref private _violationColorUrls: Set<cnl> = new Set();
	@observable.ref private _violationUrlsOfProductDueToColor: Set<cnl> = new Set();
	@observable.ref private _violationUrlsOfProduct: Set<cnl> = new Set();

	private readonly _runtimeRuleNode: C8Node;

	/** matrix path from product type to its colors */
	@computed get colorMatrixPath(): ExtendedPath | null {
		const { presentation, primaryType } = this;

		if (!presentation) {
			return null;
		}

		return getColorMatrixPath(primaryType);
	}

	/** query to apply to colors for each product */
	@computed get colorQuery(): Query | null {
		const { runtimeRules, colorMatrixPath } = this;

		// for the new presentation wizard, the rule node is not created yet, just use the query user types in directly
		return getRuntimeRulesQuery(runtimeRules, colorMatrixPath);
	}

	/** whether or not a presentation editor */
	get isPresentationEditor(): boolean {
		return isPresentationEditor();
	}

	/** presentation node */
	get presentation(): C8Node {
		return this.pageNode;
	}

	/** primary node type in PP */
	get primaryType(): string {
		return getPresentationPrimaryType(this.presentation);
	}

	/** presentation wizard store */
	get presentationWizardStore(): PPWizardStore {
		return this._presentationWizardStore;
	}

	/** product layout store */
	get productLayoutStore(): PPProductLayoutStore {
		return this._productLayoutStore;
	}

	/** properties */
	@computed get props(): IPresentationProps {
		const {
			colorQuery,
			presentation,
			primaryType,
			productCardSearch,
			productColorType,
			violationColorUrls,
			violationUrls,
			violationUrlsOfProduct,
		} = this;

		return {
			colorQuery,
			presentation,
			primaryType,
			productCardSearch,
			productColorType,
			violationUrls,
			violationUrlsOfProduct,
			violationColorUrls,
		};
	}

	/** rules for presentation */
	@computed get runtimeRules(): RuntimeRulesSchema | null {
		const { colorMatrixPath, _runtimeRuleNode } = this;

		return getRuntimeRulesFromNode(_runtimeRuleNode, colorMatrixPath);
	}

	/** search for valid products */
	@computed get productCardSearch(): Search {
		const { presentation, primaryType } = this;
		const search = getSearch("BaseProduct.ProductPresentation", presentation.$URL, { type: primaryType });

		search.addParameter("Dep.Card.Id", "Slim");
		SearchManager.getSearchTypeSortQuery(primaryType, false, search.getSortQuery());

		return search;
	}

	/** product color type for given type of product to its colors (if any) */
	@computed get productColorType(): string {
		return this.colorMatrixPath?.getEndType() || "";
	}

	/** validation store */
	@computed get validationStore(): PPValidationStore {
		return this._validationStore;
	}

	/** the url set of violations of color */
	get violationColorUrls(): Set<cnl> {
		return this._violationColorUrls;
	}

	/** the urls of violation, including the ones due to both products and colors */
	@computed get violationUrls(): Set<cnl> {
		const { violationUrlsOfProductDueToColor, violationUrlsOfProduct } = this;
		return new Set([...Array.from(violationUrlsOfProductDueToColor), ...Array.from(violationUrlsOfProduct)]);
	}

	/** the url set of violations due to color */
	get violationUrlsOfProductDueToColor(): Set<cnl> {
		return this._violationUrlsOfProductDueToColor;
	}

	/** the url set of violations due to product */
	get violationUrlsOfProduct(): Set<cnl> {
		return this._violationUrlsOfProduct;
	}

	/** set the violation set of color */
	@action setViolationColorUrls = (urlSet: Set<cnl>): void => {
		this._violationColorUrls = urlSet;
	};

	/** set the violation set of product due to color */
	@action setViolationUrlsOfProductDueToColor = (urlSet: Set<cnl>): void => {
		this._violationUrlsOfProductDueToColor = urlSet;
	};

	/** set the violation set of product */
	@action setViolationUrlsOfProduct = (urlSet: Set<cnl>): void => {
		this._violationUrlsOfProduct = urlSet;
	};

	/** create the product layout store */
	createProductLayoutStore = async (locale?: ILocaleProvider): Promise<PPProductLayoutStore> => {
		const { presentation, cache } = this;
		await processProductViolations(presentation, cache);
		const store = new PPProductLayoutStore(rootStore, presentation, null, locale);

		runInAction(() => {
			this._productLayoutStore = store;
		});

		return store;
	};

	/** create the presentation wizard store, used to support edit rule dialog, share the same store here as the wizard */
	createPresentationWizardStore = (
		locale: ILocaleProvider,
		ac?: ControllerInterface,
		onClose?: () => void,
		onSave?: ControllerSaveFunction,
		presentationUrl?: string,
		runtimeRules?: RuntimeRulesSchema,
		specs?: Spec[],
		cache?: ICache,
		postUpdateRule?: VoidFunction,

	): PPWizardStore => {
		this._presentationWizardStore = new PPWizardStore(
			locale,
			ac,
			onClose,
			onSave,
			presentationUrl,
			runtimeRules,
			specs,
			cache,
			postUpdateRule,
		);

		return this._presentationWizardStore;
	};

	/** determine whether or not disable the product */
	getDisableItemForProduct = (item: C8Node): boolean => {
		return this.getViolationOfProduct(item.$URL);
	};

	/** determine whether or not disable the color */
	getDisableItemForColor = (item: ComponentItem): boolean => {
		return this.getViolationOfColor(item.id);
	};

	/** test if this color has violation */
	getViolationOfColor = (colorUrl: cnl): boolean => {
		return this.violationColorUrls.has(colorUrl);
	};

	/** test if this product has violation */
	getViolationOfProduct = (productUrl: cnl): boolean => {
		return this.violationUrlsOfProduct.has(productUrl);
	};

	/** recalculate the violations */
	recalculateViolations = async (): Promise<void> => {
		const { cache, presentation, productLayoutStore } = this;
		await processProductViolations(presentation, cache);
		productLayoutStore.createDataStore();
	};

	constructor(node: C8Node, cache: ICache = new WorkingCache(), runtimeRuleNode: C8Node) {
		super(node, cache);
		makeObservable(this);

		this._runtimeRuleNode = runtimeRuleNode;

		this.trackReaction(reaction(() => this.violationUrls, () => {
			const { props, cache } = this;

			runInAction(() => {
				this._validationStore?.cleanupReactions();
				this._validationStore = new PPValidationStore(props, cache);
			});
		}));
	}
}